import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Minus, Plus, ArrowLeft } from "lucide-react";
import { api, imgUrl, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { FadeUp, Eyebrow } from "@/components/Motion";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setData(null);
    setActive(0);
    setQty(1);
    api.get(`/products/${id}`).then((r) => setData(r.data)).catch(() => navigate("/shop"));
  }, [id, navigate]);

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center font-serif text-2xl text-matte/40">Loading…</div>;
  }

  const { product, related } = data;
  const soldOut = product.inventory <= 0;

  const addToCart = () => {
    add(product, qty);
    toast.success(`${product.title} added to cart`);
  };

  return (
    <div data-testid="product-detail-page" className="pt-28">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Link to="/shop" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wide-sm text-matte/50 hover:text-matte mb-10" data-testid="back-to-shop">
          <ArrowLeft size={14} /> Back to Shop
        </Link>

        <div className="grid md:grid-cols-2 gap-10 md:gap-20">
          <div>
            <motion.div
              key={active}
              initial={{ opacity: 0.4 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="overflow-hidden bg-cream aspect-[4/5]"
            >
              <img src={imgUrl(product.images?.[active])} alt={product.title} className="w-full h-full object-cover" data-testid="product-main-image" />
            </motion.div>
            {product.images?.length > 1 && (
              <div className="flex gap-4 mt-4">
                {product.images.map((img) => (
                  <button
                    key={img} onClick={() => setActive(product.images.indexOf(img))}
                    className={`w-20 h-24 overflow-hidden bg-cream ${product.images[active] === img ? "ring-1 ring-plum" : "opacity-60"}`}
                  >
                    <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="md:pt-6">
            <Eyebrow>{product.category}</Eyebrow>
            <h1 className="font-serif text-4xl md:text-5xl font-light mt-3 mb-5" data-testid="product-title">{product.title}</h1>
            <p className="font-serif text-3xl text-plum mb-8" data-testid="product-price">{money(product.price)}</p>
            <p className="text-matte/65 leading-relaxed max-w-md mb-10" data-testid="product-description">{product.description}</p>

            {!soldOut ? (
              <div className="flex flex-wrap items-center gap-5">
                <div className="flex items-center border border-matte/20">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:bg-cream transition-colors" data-testid="qty-decrease"><Minus size={15} /></button>
                  <span className="w-12 text-center" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="p-3 hover:bg-cream transition-colors" data-testid="qty-increase"><Plus size={15} /></button>
                </div>
                <button
                  onClick={addToCart}
                  data-testid="add-to-cart-btn"
                  className="bg-matte text-ivory px-10 py-4 text-[12px] uppercase tracking-editorial hover:bg-plum transition-colors duration-300"
                >
                  Add to Cart
                </button>
              </div>
            ) : (
              <p className="text-[12px] uppercase tracking-wide-sm text-plum" data-testid="sold-out">Sold Out</p>
            )}
            <p className="mt-6 text-[11px] uppercase tracking-wide-sm text-matte/40">
              {product.inventory > 0 ? `${product.inventory} available` : "Unavailable"}
            </p>
          </div>
        </div>

        {related?.length > 0 && (
          <section className="py-28 md:py-40">
            <Eyebrow>Related Works</Eyebrow>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10 mt-10">
              {related.map((p) => (
                <FadeUp key={p.id}>
                  <Link to={`/shop/${p.id}`} className="group block" data-testid={`related-${p.id}`}>
                    <div className="overflow-hidden bg-cream aspect-[4/5]">
                      <img src={imgUrl(p.images?.[0])} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1200ms]" />
                    </div>
                    <h4 className="font-serif text-lg mt-4">{p.title}</h4>
                    <p className="text-sm text-matte/50">{money(p.price)}</p>
                  </Link>
                </FadeUp>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
